// ===========================================
// Email Sequence Hooks
// ===========================================
// React Query hooks for admin-authored drip series.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  cancelSequenceEnrolment,
  createEmailSequence,
  deleteEmailSequence,
  getEmailSequence,
  getEmailSequences,
  getEmailSequenceStatus,
  getSequenceEnrolments,
  getStudentSequenceEnrolments,
  pauseSequenceEnrolment,
  previewEmailSequenceStep,
  previewSequenceStart,
  resumeSequenceEnrolment,
  sendEmailSequenceTest,
  startSequenceForStudent,
  updateEmailSequence,
} from '../api/emailSequence.api';
import type {
  CreateEmailSequenceInput,
  StartEmailSequenceInput,
  StartEmailSequencePreviewInput,
  TestEmailSequenceInput,
  UpdateEmailSequenceInput,
} from '../types/emailSequence.types';

// ===========================================
// QUERY KEY FACTORY
// ===========================================

export const emailSequenceKeys = {
  all: ['emailSequence'] as const,
  status: () => [...emailSequenceKeys.all, 'status'] as const,
  lists: () => [...emailSequenceKeys.all, 'lists'] as const,
  list: (includeArchived?: boolean) => [...emailSequenceKeys.lists(), { includeArchived }] as const,
  details: () => [...emailSequenceKeys.all, 'details'] as const,
  detail: (sequenceId: string) => [...emailSequenceKeys.details(), sequenceId] as const,
  // Enrolments are keyed under `all` rather than under a sequence's detail, so
  // a lifecycle action can invalidate every enrolment view at once — the same
  // row appears on the sequence page AND on the student page.
  enrolments: () => [...emailSequenceKeys.all, 'enrolments'] as const,
  sequenceEnrolments: (sequenceId: string) =>
    [...emailSequenceKeys.enrolments(), 'sequence', sequenceId] as const,
  studentEnrolments: (studentId: string) =>
    [...emailSequenceKeys.enrolments(), 'student', studentId] as const,
  preview: (sequenceId: string, position: number) =>
    [...emailSequenceKeys.all, 'preview', sequenceId, position] as const,
};

// ===========================================
// STATUS
// ===========================================

/**
 * Whether the sweep may send on this server.
 *
 * ⚠️ Long `staleTime`: this changes only when someone edits the env and
 * restarts PM2, so re-fetching it per navigation is pure noise. It is still a
 * query rather than a constant because it differs per environment.
 */
export function useEmailSequenceStatus() {
  return useQuery({
    queryKey: emailSequenceKeys.status(),
    queryFn: getEmailSequenceStatus,
    staleTime: 5 * 60 * 1000,
  });
}

// ===========================================
// SEQUENCE CRUD
// ===========================================

export function useEmailSequences(includeArchived = false) {
  return useQuery({
    queryKey: emailSequenceKeys.list(includeArchived),
    queryFn: () => getEmailSequences(includeArchived),
  });
}

export function useEmailSequence(sequenceId: string | undefined) {
  return useQuery({
    queryKey: emailSequenceKeys.detail(sequenceId ?? ''),
    queryFn: () => getEmailSequence(sequenceId as string),
    enabled: Boolean(sequenceId),
  });
}

export function useCreateEmailSequence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEmailSequenceInput) => createEmailSequence(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailSequenceKeys.lists() });
    },
  });
}

export function useUpdateEmailSequence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sequenceId, data }: { sequenceId: string; data: UpdateEmailSequenceInput }) =>
      updateEmailSequence(sequenceId, data),
    onSuccess: (_, { sequenceId }) => {
      queryClient.invalidateQueries({ queryKey: emailSequenceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: emailSequenceKeys.detail(sequenceId) });
      // The rendered preview is derived from the steps that just changed.
      queryClient.invalidateQueries({ queryKey: [...emailSequenceKeys.all, 'preview', sequenceId] });
    },
  });
}

export function useDeleteEmailSequence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sequenceId: string) => deleteEmailSequence(sequenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailSequenceKeys.lists() });
    },
  });
}

// ===========================================
// PREVIEW & TEST
// ===========================================

export function useEmailSequenceStepPreview(
  sequenceId: string | undefined,
  position: number | undefined
) {
  return useQuery({
    queryKey: emailSequenceKeys.preview(sequenceId ?? '', position ?? -1),
    queryFn: () => previewEmailSequenceStep(sequenceId as string, position as number),
    enabled: Boolean(sequenceId) && position !== undefined && position >= 0,
  });
}

/** Sends to the logged-in admin. Nothing to invalidate — no state changes. */
export function useSendEmailSequenceTest() {
  return useMutation({
    mutationFn: ({ sequenceId, data }: { sequenceId: string; data: TestEmailSequenceInput }) =>
      sendEmailSequenceTest(sequenceId, data),
  });
}

// ===========================================
// ENROLMENTS
// ===========================================

export function useSequenceEnrolments(sequenceId: string | undefined) {
  return useQuery({
    queryKey: emailSequenceKeys.sequenceEnrolments(sequenceId ?? ''),
    queryFn: () => getSequenceEnrolments(sequenceId as string),
    enabled: Boolean(sequenceId),
  });
}

export function useStudentSequenceEnrolments(studentId: string | undefined) {
  return useQuery({
    queryKey: emailSequenceKeys.studentEnrolments(studentId ?? ''),
    queryFn: () => getStudentSequenceEnrolments(studentId as string),
    enabled: Boolean(studentId),
  });
}

/**
 * A dry run of starting a student.
 *
 * ⚠️ A MUTATION even though it changes nothing: it is a POST carrying a body,
 * and it must be re-run on demand as the admin changes the anchor date rather
 * than cached against a key.
 */
export function usePreviewSequenceStart() {
  return useMutation({
    mutationFn: ({
      sequenceId,
      data,
    }: {
      sequenceId: string;
      data: StartEmailSequencePreviewInput;
    }) => previewSequenceStart(sequenceId, data),
  });
}

export function useStartSequenceForStudent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sequenceId, data }: { sequenceId: string; data: StartEmailSequenceInput }) =>
      startSequenceForStudent(sequenceId, data),
    onSuccess: () => {
      // Every enrolment view, plus the lists (whose live counts just moved).
      queryClient.invalidateQueries({ queryKey: emailSequenceKeys.enrolments() });
      queryClient.invalidateQueries({ queryKey: emailSequenceKeys.lists() });
    },
  });
}

/**
 * Pause / resume / cancel.
 *
 * ⚠️ All three invalidate EVERY enrolment view, not just the one they were
 * called from: the same enrolment is listed on the sequence editor and on the
 * student's page, and a stale "Active" chip beside a stopped series is exactly
 * the kind of thing an admin acts on.
 */
function useEnrolmentLifecycleMutation(
  action: (enrolmentId: string) => Promise<unknown>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (enrolmentId: string) => action(enrolmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailSequenceKeys.enrolments() });
      queryClient.invalidateQueries({ queryKey: emailSequenceKeys.lists() });
    },
  });
}

export function usePauseSequenceEnrolment() {
  return useEnrolmentLifecycleMutation(pauseSequenceEnrolment);
}

export function useResumeSequenceEnrolment() {
  return useEnrolmentLifecycleMutation(resumeSequenceEnrolment);
}

export function useCancelSequenceEnrolment() {
  return useEnrolmentLifecycleMutation(cancelSequenceEnrolment);
}
